import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Sheet from 'react-modal-sheet';
import { FaImage, FaTimes, FaPlus } from 'react-icons/fa';
import { uploadImages, selectUploadStatus } from '../../store/slices/eventSlice';
import { selectAllUrlParams } from '../../store/slices/urlParamsSlice';
import { addImage } from '../../store/slices/uploadedContentSlice';
import { blobUrlToCompressedDataUrl } from '../../utils/blobToBase64';
import { uploadFilesInBatches } from '../../utils/uploadBatching';
import GuestIdentitySheet from '../GuestIdentitySheet/GuestIdentitySheet';

// No artificial client-side cap. The server accepts up to 10 files per request
// (multer config), so we accept any number and chunk into batches of 10.
// `maxImages` remains as a prop for back-compat / callers that still pass it,
// but defaults to `Infinity` so the picker no longer rejects large selections.
const ImageUploadFab = ({ maxImages = Infinity }) => {
  const [images, setImages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [identitySheetOpen, setIdentitySheetOpen] = useState(false);
  const fileInputRef = useRef();
  const sheetRef = useRef(null);
  const dispatch = useDispatch();
  const uploadStatus = useSelector(selectUploadStatus);
  const urlParams = useSelector(selectAllUrlParams);
  const theme = urlParams?.templateId || urlParams?.theme || 'event';
  const isRestaurantTheme = theme === 'restaurant';

  const handleFabClick = () => {
    if (images.length < maxImages) {
      fileInputRef.current.click();
    } else {
      setIsOpen(true);
      alert(`You can upload up to ${maxImages} images.`);
    }
  };

  const handleAddMoreImages = () => {
    if (images.length < maxImages) {
      fileInputRef.current.click();
    } else {
      alert(`You can upload up to ${maxImages} images.`);
    }
  };

  const handleFilesChange = (e) => {
    const selected = Array.from(e.target.files);

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic'];
    const validFiles = selected.filter(file => {
      const isValid = allowedTypes.includes(file.type);
      if (!isValid) {
        alert(`File "${file.name}" is not supported. Please use JPEG, PNG, or HEIC format.`);
      }
      return isValid;
    });

    const remainingSlots = Number.isFinite(maxImages) ? maxImages - images.length : validFiles.length;
    const validImages = validFiles.slice(0, Math.max(0, remainingSlots));
    const imageUrls = validImages.map(file => ({
      id: Math.random().toString(36).substring(2),
      url: URL.createObjectURL(file),
      file,
    }));
    setImages(prev => [...prev, ...imageUrls]);
    setIsOpen(true);
  };

  const handleRemove = (id) => {
    const imageToRemove = images.find(img => img.id === id);
    if (imageToRemove && imageToRemove.url) {
      URL.revokeObjectURL(imageToRemove.url); // Clean up object URL
    }
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const handleUploadClick = () => {
    if (images.length === 0) {
      alert('Please select at least one image to upload');
      return;
    }
    setIdentitySheetOpen(true);
  };

  const handleIdentitySubmit = async ({ guestName, guestPhone }) => {
    setIdentitySheetOpen(false);

    // Snapshot the picker items so we can map back from File -> object URL
    // for fast thumbnail generation after upload completes.
    const pickerItems = images;
    const files = pickerItems.map((img) => img.file);

    const { uploadedFiles, failedBatches } = await uploadFilesInBatches({
      files,
      uploadFn: uploadImages,
      dispatch,
      urlParams,
      mediaType: 'image',
      guestName,
      guestPhone,
    });

    // Generate thumbnails in PARALLEL from the existing picker object URLs
    // (the browser already decoded those for the preview grid, so this is
    // much cheaper than re-reading the underlying File via FileReader).
    const fileToObjectUrl = new Map(pickerItems.map((p) => [p.file, p.url]));
    const thumbResults = await Promise.all(
      uploadedFiles.map(async (file) => {
        const sourceUrl = fileToObjectUrl.get(file);
        try {
          return await blobUrlToCompressedDataUrl(sourceUrl, { maxWidth: 256, maxHeight: 256, quality: 0.7 });
        } catch (err) {
          console.error('Failed to create image preview:', err);
          return null;
        }
      })
    );

    uploadedFiles.forEach((file, i) => {
      const previewUrl = thumbResults[i];
      if (!previewUrl) return;
      dispatch(addImage({
        id: Date.now().toString() + Math.random().toString(36).substring(2),
        type: 'image',
        url: previewUrl,
        name: file.name,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        eventCode: urlParams.eventCode,
        guestName: guestName ?? null,
        guestPhone: guestPhone ?? null,
      }));
    });

    pickerItems.forEach((img) => URL.revokeObjectURL(img.url));
    setImages([]);
    setIsOpen(false);

    if (failedBatches.length > 0) {
      const failedCount = failedBatches.reduce((sum, b) => sum + b.batch.length, 0);
      const firstErr = failedBatches[0].error;
      const msg = typeof firstErr === 'string'
        ? firstErr
        : firstErr?.message || firstErr?.error || 'Unknown error';
      alert(`${uploadedFiles.length} uploaded, ${failedCount} failed: ${msg}`);
    }
  };

  useEffect(() => {
    if(images.length === 0) {
      setIsOpen(false);
    }
  }, [images]);

  // Cleanup object URLs when component unmounts only (intentionally empty deps)
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.url) URL.revokeObjectURL(img.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,image/heic"
        multiple
        style={{ display: 'none' }}
        onChange={handleFilesChange}
      />

      <button
        onClick={handleFabClick}
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: 'none',
          borderRadius: '50px',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          minWidth: '120px',
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.2), 0 3px 10px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)';
        }}
      >
        <FaImage style={{ color: '#2c3e50', fontSize: '20px' }} />
        <span style={{ color: '#2c3e50', fontSize: '16px', fontWeight: '600' }}>صور</span>
      </button>

      <Sheet
        ref={sheetRef}
        isOpen={isOpen}
        // Prevent swipe-down/scroll from closing the sheet.
        // Close only via backdrop tap or explicit UI actions.
        onClose={() => {}}
        snapPoints={[0.95, 0.75, 0.45]}
        initialSnap={0}
      >
        <Sheet.Container>
          <Sheet.Header />
          <Sheet.Content style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>
            {/* draggableAt="both" lets users drag the sheet up/down even while scrolling the grid */}
            <Sheet.Scroller draggableAt="both">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 20 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>صور</span>
                  <button
                    type="button"
                    onClick={() => sheetRef.current?.snapTo(0)}
                    style={{
                      border: '1px solid #e0e0e0',
                      background: '#f8fafc',
                      borderRadius: 10,
                      padding: '8px 12px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#2c3e50',
                      cursor: 'pointer',
                    }}
                  >
                    Expand ↑
                  </button>
                </div>
                <div style={sheetGridStyle}>
                  {images.map((image) => (
                    <div key={image.id} style={imageContainerStyle}>
                      <button
                        onClick={() => handleRemove(image.id)}
                        style={deleteButtonStyle}
                        disabled={uploadStatus.loading}
                      >
                        <FaTimes size={10} />
                      </button>
                      <img src={image.url} alt="uploaded" style={previewImageStyle} />
                    </div>
                  ))}
                  {images.length < maxImages && (
                    <div style={addMoreButtonContainerStyle}>
                      <button
                        onClick={handleAddMoreImages}
                        style={addMoreButtonStyle}
                        disabled={uploadStatus.loading}
                      >
                        <FaPlus size={20} />
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <button
                    style={{
                      ...uploadBtnStyle,
                      backgroundColor: uploadStatus.loading ? '#95a5a6' : '#2c3e50',
                      cursor: uploadStatus.loading ? 'not-allowed' : 'pointer',
                      opacity: uploadStatus.loading ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!uploadStatus.loading) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(44, 62, 80, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!uploadStatus.loading) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(44, 62, 80, 0.3)';
                      }
                    }}
                    onClick={handleUploadClick}
                    disabled={uploadStatus.loading}
                  >
                    {uploadStatus.loading ? 'UPLOADING...' : 'UPLOAD'}
                  </button>
                </div>
                {uploadStatus.error && (
                  <div style={errorMessageStyle}>
                    {typeof uploadStatus.error === 'string'
                      ? uploadStatus.error
                      : uploadStatus.error?.message || uploadStatus.error?.error || JSON.stringify(uploadStatus.error)}
                  </div>
                )}
              </div>
            </Sheet.Scroller>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setIsOpen(false)} />
      </Sheet>

      <GuestIdentitySheet
        isOpen={identitySheetOpen}
        onClose={() => setIdentitySheetOpen(false)}
        theme={isRestaurantTheme ? 'restaurant' : 'event'}
        onSubmit={handleIdentitySubmit}
      />
    </div>
  );
};

const sheetGridStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  padding: '10px',
};

const imageContainerStyle = {
  position: 'relative',
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
};

const deleteButtonStyle = {
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  backgroundColor: '#e74c3c',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
  boxShadow: '0 2px 8px rgba(231, 76, 60, 0.4)',
  transition: 'all 0.2s ease',
};

const previewImageStyle = {
  width: '100px',
  height: '100px',
  objectFit: 'cover',
  borderRadius: '12px',
};

const uploadBtnStyle = {
  backgroundColor: '#2c3e50',
  color: 'white',
  padding: '14px 32px',
  border: 'none',
  borderRadius: '12px',
  fontWeight: '600',
  cursor: 'pointer',
  width: '200px',
  fontSize: '16px',
  boxShadow: '0 4px 12px rgba(44, 62, 80, 0.3)',
  transition: 'all 0.2s ease',
};

const errorMessageStyle = {
  color: '#f44336',
  backgroundColor: '#ffebee',
  padding: '10px',
  borderRadius: '4px',
  marginTop: '10px',
  textAlign: 'center',
};

const addMoreButtonContainerStyle = {
  position: 'relative',
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
};

const addMoreButtonStyle = {
  width: '100px',
  height: '100px',
  border: '2px dashed #95a5a6',
  backgroundColor: 'transparent',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#7f8c8d',
  transition: 'all 0.2s ease',
};

export default ImageUploadFab;
